/**
 * Add / Edit a project file (a row in the Files tab).
 *
 * One slide-over for both create + edit, the same pattern
 * `LaminateFormSlideOver` uses.
 *
 * Fields:
 *   • File picker (required on create; optional "Replace" on edit)
 *   • Title (defaults to the picked file's name minus extension)
 *   • Category — chip picker (2D / 3D / Layout / MOM / Agreement / Other)
 *   • Description (optional notes)
 *
 * On create: upload to R2 via `uploadFileToR2({ kind: 'design' })`,
 * then `createDesign` with the returned `publicUrl` + `key`.
 * On edit: only re-upload if the user picks a new file. If they do,
 * the OLD R2 key is deleted via `deleteFromR2` after the doc write
 * succeeds, so a failed write doesn't orphan the new upload.
 * On delete: `deleteDesign` then `deleteFromR2` for cleanup.
 */
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import {
  FILE_CATEGORIES,
  createDesign,
  deleteDesign,
  fileTypeIcon,
  formatBytes,
  updateDesign,
  type Design,
  type FileCategory,
} from '@/lib/designs';
import {
  deleteFromR2,
  extFromFile,
  uploadFileToR2,
} from '@/lib/r2Upload';
import { useCurrentUser } from '@/lib/useCurrentUser';

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};
const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

export type FileFormSlideOverProps = {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** When set, the slide-over edits this doc; otherwise it creates. */
  item: Design | null;
  onSaved?: (id: string) => void;
};

/** Strip the extension off a filename for the default title. */
function titleFromFileName(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

export function FileFormSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  item,
  onSaved,
}: FileFormSlideOverProps) {
  const { user } = useAuth();
  const { userDoc } = useCurrentUser();
  const isEdit = Boolean(item);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<FileCategory>('design_2d');

  // Local file selection — null when no new file picked. On edit
  // this stays null until the user clicks "Replace file" + selects
  // a new one. On create the user MUST pick a file.
  const [pickedFile, setPickedFile] = useState<File | null>(null);

  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset / preload on every open.
  useEffect(() => {
    if (!open) return;
    if (item) {
      setTitle(item.title);
      setDescription(item.description ?? '');
      setCategory(item.category);
    } else {
      setTitle('');
      setDescription('');
      setCategory('design_2d');
    }
    setPickedFile(null);
    setConfirmDelete(false);
    setError(null);
  }, [open, item]);

  // Body scroll-lock.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc closes (unless busy).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  function handlePickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = ''; // allow re-picking same file
    if (!file) return;
    setError(null);
    setPickedFile(file);
    // Default title only when creating (don't overwrite a user-
    // chosen title on edit).
    if (!isEdit && !title.trim()) {
      setTitle(titleFromFileName(file.name));
    }
    // Auto-categorise on first pick — best-effort heuristic from
    // mime/extension. User can override.
    if (!isEdit) {
      setCategory(suggestCategory(file));
    }
  }

  async function handleSubmit() {
    setError(null);
    if (!user) return setError('Not signed in.');
    if (!orgId) return setError('No organisation context.');
    if (!title.trim()) return setError('Title is required.');
    if (!isEdit && !pickedFile) {
      return setError('Pick a file to upload.');
    }

    setBusy(true);
    try {
      const createdByName =
        (userDoc?.data?.displayName as string | undefined)?.trim() ||
        user.email ||
        'You';

      if (isEdit && item) {
        // Optional re-upload path.
        let newPayload:
          | { url: string; key: string; contentType: string; sizeBytes: number; name?: string }
          | undefined;
        let oldKeyToDelete: string | undefined;
        if (pickedFile) {
          const { publicUrl, key } = await uploadFileToR2({
            file: pickedFile,
            kind: 'design',
            refId: item.id,
            orgId,
          });
          newPayload = {
            url: publicUrl,
            key,
            contentType:
              pickedFile.type || 'application/octet-stream',
            sizeBytes: pickedFile.size,
            name: pickedFile.name,
          };
          oldKeyToDelete = item.fileKey;
        }

        await updateDesign(item.id, {
          title,
          description,
          category,
          file: newPayload,
        });

        // Best-effort cleanup of the replaced R2 object. Don't
        // surface errors — the new doc write already succeeded.
        if (oldKeyToDelete) {
          try {
            await deleteFromR2({
              projectId,
              key: oldKeyToDelete,
              kind: 'design',
              refId: item.id,
              sizeBytes: item.fileSizeBytes,
              contentType: item.fileContentType,
            });
          } catch (e) {
            console.warn('[file] old R2 key cleanup failed:', e);
          }
        }
        onSaved?.(item.id);
      } else if (pickedFile) {
        // Create flow — upload first, then write the doc.
        // refId is unknown until createDesign runs, so we use a
        // temporary refId. R2 cap check uses orgId.
        const tempRefId = `new-${Date.now()}`;
        const { publicUrl, key } = await uploadFileToR2({
          file: pickedFile,
          kind: 'design',
          refId: tempRefId,
          orgId,
        });
        const newId = await createDesign({
          orgId,
          projectId,
          title,
          description,
          category,
          file: {
            url: publicUrl,
            key,
            contentType: pickedFile.type || 'application/octet-stream',
            sizeBytes: pickedFile.size,
            name: pickedFile.name,
          },
          createdBy: user.uid,
          createdByName,
        });
        onSaved?.(newId);
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[file] save failed:', err);
      setError(msg || 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!item) return;
    setBusy(true);
    setError(null);
    try {
      const { fileKey, fileSizeBytes, fileContentType, id } = item;
      await deleteDesign(id);
      // Cleanup R2 — best-effort.
      if (fileKey) {
        try {
          await deleteFromR2({
            projectId,
            key: fileKey,
            kind: 'design',
            refId: id,
            sizeBytes: fileSizeBytes,
            contentType: fileContentType,
          });
        } catch (e) {
          console.warn('[file] R2 cleanup failed on delete:', e);
        }
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[file] delete failed:', err);
      setError(msg || 'Delete failed.');
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  // Display data for the file preview line — picked file (new),
  // OR the existing item's file (edit, no new pick).
  const previewName = pickedFile?.name ?? item?.fileName ?? '';
  const previewSize = pickedFile?.size ?? item?.fileSizeBytes ?? 0;
  const previewCt =
    pickedFile?.type ?? item?.fileContentType ?? 'application/octet-stream';
  const previewIcon = fileTypeIcon(previewCt, previewName);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-file" />{' '}
            {isEdit ? 'Edit File' : 'Upload File'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: '#FEF2F2',
                color: 'var(--critical)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                border: '1px solid #FECACA',
              }}
            >
              {error}
            </div>
          ) : null}

          {/* File picker / preview */}
          <div>
            <label style={LABEL}>File {isEdit ? '' : '*'}</label>

            {previewName ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  padding: 12,
                  background: '#FAFAFA',
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    color: previewIcon.color,
                    background: '#fff',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                  }}
                >
                  <i className={previewIcon.icon} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {previewName || 'Selected file'}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      fontFamily: 'Menlo, monospace',
                      marginTop: 2,
                    }}
                  >
                    {formatBytes(previewSize)}
                    {pickedFile ? ' · new upload' : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  style={{ flexShrink: 0 }}
                >
                  <i className="ph ph-arrows-clockwise" /> Replace
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                style={{
                  width: '100%',
                  border: '2px dashed var(--border)',
                  borderRadius: 8,
                  background: '#FAFAFA',
                  padding: '24px 16px',
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <i
                  className="ph ph-upload-simple"
                  style={{ fontSize: 28, color: 'var(--text-tertiary)' }}
                />
                <span style={{ fontWeight: 600 }}>Choose a file to upload</span>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  PDF · image · DWG · DOC · XLS · ZIP · anything
                </span>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              onChange={handlePickFile}
              style={{ display: 'none' }}
              accept="*/*"
            />
          </div>

          {/* Title */}
          <div>
            <label style={LABEL}>Title *</label>
            <input
              style={INPUT}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
              placeholder="e.g. Master Bedroom Electrical Layout"
              maxLength={120}
            />
          </div>

          {/* Category — chip picker */}
          <div>
            <label style={LABEL}>Category *</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {FILE_CATEGORIES.map((c) => {
                const active = c.key === category;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setCategory(c.key)}
                    style={{
                      padding: '5px 12px',
                      border: active
                        ? '1.5px solid var(--brand)'
                        : '1px solid var(--border)',
                      background: active ? 'var(--brand-bg)' : '#fff',
                      color: active ? 'var(--brand)' : 'var(--text-secondary)',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: active ? 700 : 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={LABEL}>Notes</label>
            <textarea
              style={{ ...INPUT, minHeight: 80, resize: 'vertical' }}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              placeholder="What's this file? Who is it for? Any context the team should know."
              maxLength={500}
            />
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            {isEdit ? (
              confirmDelete ? (
                <div
                  style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                >
                  <span style={{ fontSize: 12, color: 'var(--critical)' }}>
                    Delete this file?
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmDelete(false)}
                    disabled={busy}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleDelete}
                    disabled={busy}
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    Yes, delete
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Delete
                </button>
              )
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              {item?.fileUrl ? (
                <a
                  className="btn-secondary"
                  href={item.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={item.fileName ?? undefined}
                  style={{ textDecoration: 'none' }}
                >
                  <i className="ph ph-download-simple" /> Download
                </a>
              ) : null}
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmit}
                disabled={busy}
              >
                {busy
                  ? 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : 'Upload file'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ───── Helpers ─────

/** Heuristic category from a picked file's mime / extension. */
function suggestCategory(file: File): FileCategory {
  const ct = (file.type || '').toLowerCase();
  const ext = (extFromFile(file) || '').toLowerCase();
  if (ct === 'application/pdf' || ext === 'pdf') {
    // PDFs default to agreement (the most common reason to upload
    // a PDF to a project) — user can flip via chip.
    return 'agreement';
  }
  if (ct.startsWith('image/')) {
    return 'design_3d';
  }
  if (['dwg', 'dxf'].includes(ext)) return 'design_2d';
  if (['rvt', 'skp', '3ds', 'obj'].includes(ext)) return 'design_3d';
  if (['doc', 'docx'].includes(ext)) return 'mom';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'agreement';
  return 'other';
}
